import React, { useContext, useState, useEffect, useReducer, useCallback, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Body, Box, Button, Card, CollectionFilters, Pagination, SearchResultsGrid, TwoColumn, breakpoints, red, grey400, grey600 } from 'dma-ui-kit';
import axios from 'axios';
import styled from 'styled-components';
import SiteContext from 'site/SiteContext';
import { translateInterfaceText } from 'helpers/translation';
import { filterQuery, history, unpackFilterQuery } from 'helpers/search';
import { buildIIIFImageURL } from 'helpers/collection';
import ImageNotFound from 'assets/missing.png';
import { reducer as queryReducer } from 'search/queries';
import { ConditionalWrap } from 'helpers/ConditionalWrap';
import useBreakpoint from 'use-breakpoint';

const InputContainer = styled(Box)`
  display: flex;
  padding: 10px 0;
  align-items: baseline;
  position: relative;
  width: 100%;
`;

const SuggestionsDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid ${grey400};
  border-top: none;
  max-height: 200px;
  overflow-y: auto;
  z-index: 2002;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const SuggestionItem = styled.div`
  padding: 12px 16px;
  cursor: pointer;
  border-bottom: 1px solid ${grey400};
  transition: background-color 0.2s ease;
  background-color: ${props => props.$active ? grey600 : 'transparent'};
  
  &:hover {
    background-color: ${grey600};
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const SuggestionText = styled.div`
  font-size: 1.2rem;
  font-weight: 400;
  color: ${red};
  margin-bottom: 4px;
`;

const Input = styled.input`
  font-family: 'usual';
  font-weight: 400;
  font-size: 2rem;
  width: 100%;
  font-style: italic;
  margin-right: 20px;
  box-sizing: border-box;
  padding: 10px;
  color: ${red};
  background-color: transparent;
  border: none;
  border-bottom: 4px solid ${red};

  &:focus {
    outline: none;
    border-bottom: 4px solid ${red};
  }

  ::placeholder {
    font-style: none;
    color: ${grey400};
  }
`;

const Container = styled(Box)`
  padding: 0 12px;

  @media (min-width: 1025px) {
      padding: 0 48px;
  }
  @media (max-width: 1024px) {
      padding: 0 24px;
  }    

  margin-bottom: 10px;
  display: block;
  position: relative;
`;

const EmptyCell = styled.div`
  display: flex;
  flex-direction: column;
  width: 204px;
  padding-left: 0;
  position: relative;
`;

const PaginationContainer = styled(Box)`
  max-width: 320px;
  margin: auto;
  margin-top: 48px;

  @media (min-width: 1025px) {
      max-width: 800px;
  }
  @media (max-width: 1024px) {
      max-width: 640px;
  }    
`;

function compareKeys(a, b) {
  return (a.key < b.key) ? -1 : (a.key > b.key) ? 1 : 0;
}

const rowMapping = {
  mobile: 2,
  tabletSmall: 3,
  tablet: 3,
  desktop: 3,
  desktopLarge: 4
};

const CollectionSearch = React.memo(() => {

  const { displayLanguage } = useContext(SiteContext);
  const searchEndpoint = `${process.env.REACT_APP_SITESEARCH_BASE_PATH}api/search/collection`;
  const suggestURL = `${process.env.REACT_APP_SITESEARCH_BASE_PATH}api/search/collection/suggest`;
        
    const noResultsText = translateInterfaceText("No Results Found");
    const loadingText = translateInterfaceText("Loading");
    const searchText = translateInterfaceText('Search');
    const enterSearchTermText = translateInterfaceText('Enter search term');

    const artistText = translateInterfaceText("Artist");
    const cultureText = translateInterfaceText("Culture");
    const departmentText = translateInterfaceText("Department");
    const mediumText = translateInterfaceText("Medium");
    const onViewText = translateInterfaceText("On View");

    const { search } = useLocation();
    const params = new URLSearchParams(search);

    const initialTerm = params.get('s');
    const initialFilter = unpackFilterQuery(params.get('f'));
    const initialPage = params.get('p');

    const [searchTerm, setSearchTerm] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const [results, setResults] = useState({});
    const [loading, setLoading] = useState(false);

    const debounceTimeoutRef = useRef(null);
    const suggestionsRef = useRef(null);

    const [options, dispatch] = useReducer(
      queryReducer,
      {  
        type: 'collection',
        page: initialPage || 1,
        term: initialTerm || '',
        limit: 20,
        filters: initialFilter || {}
      },  
    );

    useEffect(() => {
      let active = true;

      const fetchResults = async () => {
        setLoading(true);

        try {
          const queryParams = new URLSearchParams();
          const pageLimit = options.limit || 20;
          const currentPage = options.page || 1;

          queryParams.append('size', pageLimit);
          queryParams.append('from', (currentPage - 1) * pageLimit);

          if (options.term) {
            queryParams.append('q', options.term);
          }

          if (options.filters && Object.keys(options.filters).length > 0) {
            queryParams.append('filters', JSON.stringify(options.filters));
          }

          if (displayLanguage && displayLanguage !== 'en') {
            queryParams.append('l', displayLanguage);
          }

          const response = await axios.get(`${searchEndpoint}?${queryParams.toString()}`);

          if (!active) return;

          setResults({
            hits: response.data.hits || response.data.results || [],
            resultCount: response.data.total || response.data.pagination?.total || 0,
            pageCount: response.data.pageCount || response.data.pagination?.totalPages || 1,
            currentPage: response.data.currentPage || response.data.pagination?.currentPage || currentPage,
            aggregations: response.data.aggregations || {},
          });
        } catch (error) {
          if (active) {
            console.error('Error fetching collection search results:', error);
            setResults({ hits: [], aggregations: {}, pageCount: 1, currentPage: 1, resultCount: 0 });
          }
        } finally {
          if (active) {
            setLoading(false);
          }
        }
      };

      fetchResults();

      return () => {
        active = false;
      };
    }, [displayLanguage, options, searchEndpoint]);

    // Fetch suggestions from API
    const fetchSuggestions = useCallback(async (term) => {
      if (!term || term.length < 2) {
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }

      try {
        const response = await fetch(`${suggestURL}?q=${encodeURIComponent(term)}`);
        if (response.ok) {
          const data = await response.json();
          if (data.suggestions && Array.isArray(data.suggestions)) {
            setSuggestions(data.suggestions);
            setActiveIndex(-1);
            setShowSuggestions(true);
          }
        }
      } catch (error) {
        console.error('Error fetching suggestions:', error);
        setSuggestions([]);
      }
    }, [suggestURL]);

    // Debounced suggestion fetching
    const debouncedFetchSuggestions = useCallback((term) => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      
      debounceTimeoutRef.current = setTimeout(() => {
        fetchSuggestions(term);
      }, 300);
    }, [fetchSuggestions]);

    // Handle clicks outside suggestions dropdown
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
          setShowSuggestions(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current);
        }
      };
    }, []);

    const handleFilterChange = ( {filter, item} ) => {
      const { filters } = options;
      const payload = filters ? { ...filters } : { };

      // Delete the value if it matches current, otherwise overlay
      if (payload[filter] && payload[filter] == item)
        delete payload[filter];
      else
        payload[filter] = [ item ];
      
      dispatch({ type: 'setFilters', payload });
      dispatch({ type: 'setPagination', payload: { page: 1, limit: options.limit }});
      history.push(`/art/collection/search?s=${options.term}&f=${filterQuery(payload)}&p=1`);
      window.scrollTo(0, 0);
    };

    const handleTermChange = e => {
      const value = e.target.value;
      setSearchTerm(value);
      setActiveIndex(-1);
      debouncedFetchSuggestions(value);
    };

    const handleKeyDown = (e) => {
      const open = showSuggestions && suggestions.length > 0;

      if (e.key === 'ArrowDown' && open) {
        e.preventDefault();
        setActiveIndex(prev => (prev + 1) % suggestions.length);
      } else if (e.key === 'ArrowUp' && open) {
        e.preventDefault();
        setActiveIndex(prev => (prev <= 0 ? suggestions.length - 1 : prev - 1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (open && activeIndex >= 0 && activeIndex < suggestions.length) {
          handleSuggestionClick(suggestions[activeIndex]);
        } else {
          handleTermSubmit();
        }
        setShowSuggestions(false);
        setActiveIndex(-1);
      } else if (e.key === 'Escape') {
        setShowSuggestions(false);
        setActiveIndex(-1);
      }
    };

    const handleSuggestionClick = (suggestion) => {
      setSearchTerm(suggestion);
      setShowSuggestions(false);
      handleTermSubmit(suggestion);
    };

    const handleTermSubmit = (val) => {
      const term = typeof val === 'string' ? val : searchTerm;
      setSearchTerm('');
      setSuggestions([]);
      setShowSuggestions(false);
      dispatch({ type: 'setTerm', payload: term });
      dispatch({ type: 'setFilters', payload: {} });
      dispatch({ type: 'setPagination', payload: { page: 1, limit: options.limit }});
      history.push(`/art/collection/search?s=${term}&f=${filterQuery({})}&p=1`);
      window.scrollTo(0, 0);
    };

    const handlePageChange = val => {
      dispatch({ type: 'setPagination', payload: { page: val, limit: options.limit }});
      history.push(`/art/collection/search?s=${options.term}&f=${filterQuery(options.filters)}&p=${val}`);
      window.scrollTo(0, 0);
    };

    const { breakpoint } = useBreakpoint(breakpoints);
    
    return (
      <Container>
        <Box mb="24px" style={{position: 'relative', zIndex: 10}}>
          <InputContainer ref={suggestionsRef}>
            <Input
              onChange={handleTermChange}
              onKeyDown={handleKeyDown}
              onFocus={() => {
                if (suggestions.length > 0 && searchTerm.length >= 2) {
                  setShowSuggestions(true);
                }
              }}
              type={'text'}
              value={searchTerm}
              placeholder={options?.term ? options.term : enterSearchTermText}
            />
            <Box>
              <Button
                variant="outline"
                label={searchText}
                onClick={handleTermSubmit}
                compact={true}
                color={red}
              />
            </Box>
            {showSuggestions && suggestions.length > 0 && (
              <SuggestionsDropdown>
                {suggestions.map((suggestion, index) => (
                  <SuggestionItem
                    key={index}
                    onClick={() => handleSuggestionClick(suggestion)}
                    onMouseEnter={() => setActiveIndex(index)}
                    $active={index === activeIndex}
                  >
                    <SuggestionText>{suggestion}</SuggestionText>
                  </SuggestionItem>
                ))}
              </SuggestionsDropdown>
            )}
          </InputContainer>
        </Box>
        
        {(() => {
          const filters = {
            artist: { label: artistText, options: [] },
            culture: { label: cultureText, options: [] },
            department: { label: departmentText, options: [] },
            medium: { label: mediumText, options: [] },
            onview: { label: onViewText, options: [] }
          };

          filters.onview.options['On View'] = {
            count: (results?.aggregations?.onview?.doc_count) ? results.aggregations.onview.doc_count : 0,
            selected: ((options.filters.onview == 'On View') ? true : false)
          };

          if (results?.aggregations?.artist?.name)
            results.aggregations.artist.name.sort(compareKeys).map(agg => {
              if (agg.key.length > 0)
                filters.artist.options[agg.key] = {
                  count: agg.doc_count,
                  selected: ((options.filters.artist == agg.key) ? true : false)
                };
            });

          if (results?.aggregations?.culture)
            results.aggregations.culture.sort(compareKeys).map(agg => {
              if (agg.key.length > 0)
                filters.culture.options[agg.key] = {
                  count: agg.doc_count,
                  selected: ((options.filters.culture == agg.key) ? true : false)
                };
            });

          if (results?.aggregations?.department)
            results.aggregations.department.sort(compareKeys).map(agg => {
              if (agg.key.length > 0)
                filters.department.options[agg.key] = {
                  count: agg.doc_count,
                  selected: ((options.filters.department == agg.key) ? true : false)
                };
            });

          if (results?.aggregations?.medium)
            results.aggregations.medium.sort(compareKeys).map(agg => {
              if (agg.key.length > 0)
                filters.medium.options[agg.key] = {
                  count: agg.doc_count,
                  selected: ((options.filters.medium == agg.key) ? true : false)
                };
            });

          return (
            <>
              {results?.hits && results.hits.length > 0 && !loading && (
                <ConditionalWrap
                  condition={breakpoint.includes('desktop')}
                  wrap={children => <TwoColumn variant="wide-right">{children}</TwoColumn>}
                >

                  <CollectionFilters filters={filters} filterLimit={5} onChange={handleFilterChange} />
                  <div>
                    <SearchResultsGrid cardsPerRow={rowMapping[breakpoint]}>
                      {results.hits.map((result, index) => {
                        const imgSrc = (result.primary_image == -1) ? ImageNotFound : buildIIIFImageURL(result.id, result.primary_image, { size: 200, type: 'square' });

                        const constituent = result.constituents
                          ? result.constituents
                              .filter(c => c.role !== "Depicted individual")
                              .map(c => c.name)
                              .join('\n')
                          : '';

                        return (
                          <Link key={index} to={{ pathname: `object/${result.id}` }}>
                            <Card
                              title={result.title}
                              caption={constituent}
                              image={imgSrc}
                              variant="mini"
                            />
                          </Link>
                        );
                      })}
                      {(() => {
                        const cardsPerRow = rowMapping[breakpoint];
                        if (results.hits.length >= cardsPerRow) return null;
                        return Array.from({ length: cardsPerRow - results.hits.length }, (_, i) => (
                          <EmptyCell key={`filler-${i}`} variant="mini" />
                        ));
                      })()}
                    </SearchResultsGrid>
                    <PaginationContainer>
                      <Pagination
                        currentPage={results?.currentPage || 1}
                        pageCount={results?.pageCount || 1}
                        onChange={handlePageChange}
                      />
                    </PaginationContainer>
                  </div>
                </ConditionalWrap>
              )}
              {results?.hits?.length === 0 && !loading && (
                <Box w="100%" pt="48px">
                  <Body textAlign="center">{noResultsText}</Body>
                </Box>
              )}
              {loading && (
                <Box w="100%" pt="48px">
                  <Body textAlign="center">{loadingText}...</Body>
                </Box>
              )}
            </>
          );
        })()}
      </Container>
    );
});

export default CollectionSearch;
